import { computed, observable } from 'mobx';
import { useMount } from 'ahooks';
import { autobind } from 'core-decorators';
import CookieSettingModalStore from './components/cookie-setting-modal/store';
import { useEffect } from 'react';
import Cookie from '@src/utils/cookie';
import { listenCustomEvent } from '@src/utils/custom-event';

export const COOKIE_FLAG = 'cookie_flag';

class CookieTipsStore {
  cookieSettingModalStore = new CookieSettingModalStore();
  /**
   *
   * @type {boolean}
   */
  @observable isDirectToSettingModal = false;

  @observable cookieFlagInfo;
  /**
   * 是否显示modal
   * @type {boolean}
   */
  @observable showModalStatus = false;
  /**
   * 设置
   * @type {boolean}
   */
  @observable showCookieSettings = true;

  constructor({ ...rest } = {}) {
    Object.assign(this, rest);
  }

  @observable setCookieDescription = '';

  /**
   * 是否允许cookies
   * @type {{analytics: boolean, necessary: boolean}}
   */
  @observable
  acceptCookies = {
    necessary: true,
    analytics: false
  };

  /**
   * 关闭按钮
   * @type {function()}
   */
  @autobind
  onClose() {
    this.showModalStatus = false;
  }

  @autobind
  changeShowModalStatus(status) {
    this.showModalStatus = status;
  }

  @autobind
  onCookieSettingShowClick() {
    this.cookieSettingModalStore.isShow = true;
    this.showCookieSettings = false;
    this.getCookieFlag();
    this.init();
  }

  @autobind
  backToCookieTipModal() {
    this.cookieSettingModalStore.isShow = false;
    this.showCookieSettings = true;
  }

  /**
   * 是否存在CookieFlag
   * @returns {boolean}
   */
  @computed
  get hasCookieFlag() {
    return !!this.cookieFlagInfo;
  }

  /**
   * 标记cookie是否已经选择
   */
  @autobind
  setCookieFlag(status = { necessary: 1, analytics: 0 }) {
    Cookie.set(COOKIE_FLAG, status, { expires: 90 });
    // Cookie.set(COOKIE_FLAG, status, { expires: new Date(new Date().getTime() + 5 * 60 * 1000) });
    this.acceptCookies.analytics = status.analytics;
    this.acceptCookies.necessary = status.necessary;
  }

  @autobind
  getCookieFlag() {
    this.cookieFlagInfo = Cookie.get(COOKIE_FLAG);
  }

  /**
   * 点击"拒绝可选cookie"按钮时回调
   */
  @autobind
  rejectOptionalCookies() {
    this.setCookieFlag({ necessary: 1, analytics: 0 });
    this.changeShowModalStatus(false);
  }

  /**
   * 点击"接受所有的cookie"按钮时回调
   */
  @autobind
  acceptOptionalCookies() {
    this.setCookieFlag({ necessary: 1, analytics: 1 });
    this.changeShowModalStatus(false);
    this.injectGAScript();
  }

  /**
   * 注入Google Analytics脚本
   */
  @autobind
  injectGAScript() {
    // 检查脚本是否已注入，避免重复注入
    const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    if (existingScript) {
      console.log('Google Analytics脚本已存在，无需重复注入');
      return;
    }
    // 创建gtag.js脚本标签
    const gtagScript = document.createElement('script');
    // 手动修改,每个专题的GA_ID不同
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-L16CWS5XQR';
    gtagScript.async = true;
    // 创建配置脚本标签
    const configScript = document.createElement('script');
    configScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }
    gtag('js', new Date());
    gtag('config', 'G-L16CWS5XQR');
  `;
    // 将脚本添加到head中
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(gtagScript);
    head.appendChild(configScript);

    console.log('Google Analytics脚本已成功注入');
  }

  /**
   * 初始化
   */
  @autobind
  init() {
    if (this.hasCookieFlag) {
      const info = JSON.parse(this.cookieFlagInfo);
      this.acceptCookies.necessary = info.necessary;
      this.acceptCookies.analytics = info.analytics;
      this.cookieSettingModalStore.acceptCookies.analytics = info.analytics;
      this.cookieSettingModalStore.acceptCookies.necessary = info.necessary;
    } else {
      this.acceptCookies.necessary = 1;
      this.acceptCookies.analytics = 0;
      this.cookieSettingModalStore.acceptCookies.analytics = 0;
      this.cookieSettingModalStore.acceptCookies.necessary = 1;
    }
  }

  use() {
    useMount(() => {
      this.showCookieSettings = true;
      this.getCookieFlag();
      this.cookieSettingModalStore.acceptOptionalCookies = this.acceptOptionalCookies;
      this.cookieSettingModalStore.rejectOptionalCookies = this.rejectOptionalCookies;
      this.cookieSettingModalStore.injectGAScript = this.injectGAScript;
      this.init();

      // 监听自定义事件
      const destroyListener = listenCustomEvent('omni:footer:cookie_settings', () => {
        console.log('接收到cookie设置事件');
        this.showModalStatus = true;
        this.onCookieSettingShowClick();
      });

      // 在组件卸载时移除监听器
      return () => {
        destroyListener();
      };
    });

    useEffect(
      () => {
        if (!this.hasCookieFlag) {
          this.changeShowModalStatus(true);
        }
      },
      [this.hasCookieFlag]
    );
    useEffect(
      () => {
        this.getCookieFlag();
        this.init();
      },
      [this.isDirectToSettingModal]
    );
    useEffect(
      () => {
        this.cookieSettingModalStore.acceptCookies.analytics = this.acceptCookies.analytics;
        this.cookieSettingModalStore.acceptCookies.necessary = this.acceptCookies.necessary;
      },
      [this.acceptCookies.analytics, this.acceptCookies.necessary]
    );
  }
}

export default CookieTipsStore;
